package dev.lingvo.shared

/** Parse outcomes distinguish unrelated archive entries from screenshots with incomplete metadata. */
sealed interface FastlaneParse {
    /** Optional device and ordering fields let the upload matcher ask the user about ambiguous files. */
    data class Entry(
        val storeLocale: String, val device: DeviceClass?, val position: Int?, val slotKey: String?,
    ) : FastlaneParse
    /** Explains why an archive entry must not be imported. */
    data class Ignored(val reason: String) : FastlaneParse
}

/** One path convention prevents export, server import and browser upload matching from drifting. */
object FastlaneLayout {
    private val localePattern = Regex("^[A-Za-z]{2,3}(?:-[A-Za-z0-9]{2,8}){0,2}$")
    private val keyPattern = Regex("^[a-z0-9][a-z0-9_-]{0,47}$")
    private val orderedName = Regex("^([0-9]{2})_(.+)$")

    /** Builds only relative, traversal-free paths with deterministic deliver ordering. */
    fun exportPath(
        platform: StorePlatform, layout: ShotExportLayout, kind: ShotExportKind, storeLocale: String,
        device: DeviceClass, position: Int, slotKey: String, format: ImageFormat,
    ): String {
        require(device.platform == platform) { "Device does not belong to platform" }
        require(localePattern.matches(storeLocale)) { "Invalid store locale" }
        require(keyPattern.matches(slotKey)) { "Invalid slot key" }
        require(position in 0 until if (device == DeviceClass.PLAY_FEATURE_GRAPHIC) 8 else ScreenshotSpecs.of(device).maxPerLocale) { "Position exceeds the store set limit" }
        val number = (position + 1).toString().padStart(2, '0')
        val name = "${number}_${slotKey}.${format.extension}"
        return when (layout) {
            ShotExportLayout.PLAIN -> "${kind.name.lowercase()}/${platform.id}/$storeLocale/${device.id}/$name"
            ShotExportLayout.FASTLANE -> when (platform) {
                StorePlatform.APP_STORE -> "fastlane/screenshots/$storeLocale/${number}_${device.id}_${slotKey}.${format.extension}"
                StorePlatform.GOOGLE_PLAY -> if (device == DeviceClass.PLAY_FEATURE_GRAPHIC) {
                    "fastlane/metadata/android/$storeLocale/images/featureGraphic.${format.extension}"
                } else "fastlane/metadata/android/$storeLocale/images/${ScreenshotSpecs.of(device).storeType}/$name"
            }
        }
    }

    /** Matches known layouts without normalising unsafe archive paths into apparently safe ones. */
    fun parse(platform: StorePlatform, path: String, size: PixelSize?): FastlaneParse {
        if (path.length > 1024 || path.startsWith('/') || '\\' in path || Regex("^[A-Za-z]:").containsMatchIn(path)) {
            return FastlaneParse.Ignored("Unsafe archive path")
        }
        val parts = path.split('/')
        if (parts.any { it.isEmpty() || it == "." || it == ".." || it.any { c -> c.code < 32 || c.code in 127..159 } }) {
            return FastlaneParse.Ignored("Unsafe path component")
        }
        if (parts.any { it == ".DS_Store" || it == "__MACOSX" }) return FastlaneParse.Ignored("Archive metadata")
        val filename = parts.last()
        val extension = filename.substringAfterLast('.', "").lowercase()
        if (extension !in setOf("png", "jpg", "jpeg")) return FastlaneParse.Ignored("Not a PNG or JPEG")
        val stem = filename.substringBeforeLast('.')
        if (extension == "png" && stem.lowercase().endsWith("_framed")) return FastlaneParse.Ignored("Pre-framed screenshot")
        val locale: String
        var device: DeviceClass? = null
        val appleName: Boolean
        when {
            parts.size == 4 && parts.take(2) == listOf("fastlane", "screenshots") && platform == StorePlatform.APP_STORE -> {
                locale = parts[2]
                appleName = true
            }
            parts.size == 3 && parts[0] == "screenshots" -> {
                locale = parts[1]
                appleName = platform == StorePlatform.APP_STORE
            }
            parts.size == 6 && parts.take(3) == listOf("fastlane", "metadata", "android") &&
                parts[4] == "images" && stem == "featureGraphic" && platform == StorePlatform.GOOGLE_PLAY -> {
                if (!localePattern.matches(parts[3])) return FastlaneParse.Ignored("Invalid store locale")
                return FastlaneParse.Entry(parts[3], DeviceClass.PLAY_FEATURE_GRAPHIC, null, null)
            }
            parts.size == 7 && parts.take(3) == listOf("fastlane", "metadata", "android") &&
                parts[4] == "images" && platform == StorePlatform.GOOGLE_PLAY -> {
                locale = parts[3]
                device = DeviceClass.forPlatform(platform).firstOrNull { ScreenshotSpecs.of(it).storeType == parts[5] }
                    ?: return FastlaneParse.Ignored("Unknown Play image type")
                appleName = false
            }
            parts.size == 5 && parts[0] in setOf("captures", "renders") && parts[1] == platform.id -> {
                locale = parts[2]
                device = DeviceClass.fromId(parts[3])?.takeIf { it.platform == platform }
                    ?: return FastlaneParse.Ignored("Unknown or foreign device class")
                appleName = false
            }
            else -> return FastlaneParse.Ignored("Unrecognised screenshot layout or excessive depth")
        }
        if (!localePattern.matches(locale)) return FastlaneParse.Ignored("Invalid store locale")
        val ordered = orderedName.matchEntire(stem)
        val position = ordered?.groupValues?.get(1)?.toInt()?.takeIf { it > 0 }?.minus(1)
        var key = ordered?.groupValues?.get(2)
        if (appleName) {
            val text = key ?: stem
            val named = DeviceClass.entries.sortedByDescending { it.id.length }.firstOrNull {
                text == it.id || text.startsWith("${it.id}_")
            }
            if (named != null) {
                if (named.platform != platform) return FastlaneParse.Ignored("Foreign device class")
                device = named
                key = text.removePrefix(named.id).removePrefix("_").takeIf { keyPattern.matches(it) }
            }
            if (device == null && size != null) {
                device = sequenceOf(ShotPurpose.STORE, ShotPurpose.CAPTURE).flatMap { purpose ->
                    DeviceClass.forPlatform(platform).asSequence().filter {
                        ScreenshotSpecs.accepts(ScreenshotSpecs.rule(it, purpose), size)
                    }
                }.firstOrNull()
            }
        }
        return FastlaneParse.Entry(locale, device, position, key?.takeIf { keyPattern.matches(it) })
    }
}
